// ---------------------------------------------------------------------------
// BlobListRenderer — Generic fallback used when no renderer is registered
// for a given `generationType`. Reproduces the pre-renderer UX exactly:
//
//   - "Summary": a single line "<N> example(s) generated".
//   - "Items":   one card per blob; JSON blobs are pretty-printed in a <pre>,
//                non-JSON blobs (e.g. .gml) show only the name + "(not loaded)".
//   - "toDataEntries": one DataEntry per JSON blob, raw text in
//                      `properties.content`. GML is intentionally skipped.
//
// This is the safety net for adding new generation types without blocking on
// a custom renderer.
// ---------------------------------------------------------------------------

import { Body1, Spinner } from '@fluentui/react-components';
import { useMemo } from 'react';

import type { CreateDataEntryRequest } from '../../../../services/dataHubService.interface.js';
import type {
  SyntheticRendererContext,
  SyntheticResultRenderer,
} from '../renderer.interface.js';

import { useStyles } from './BlobListRenderer.style.js';

function isJsonBlob(name: string): boolean {
  return name.toLowerCase().endsWith('.json');
}

function Summary({ result }: SyntheticRendererContext) {
  const styles = useStyles();
  const count = result.messagesGenerated ?? 0;
  return (
    <Body1 className={styles.summary}>
      {count.toLocaleString()} example{count === 1 ? '' : 's'} generated
    </Body1>
  );
}

function Items({ result, blobs }: SyntheticRendererContext) {
  const styles = useStyles();
  const files = useMemo(() => {
    return (result.blobs ?? []).map((name) => ({
      name,
      kind: isJsonBlob(name) ? ('json' as const) : ('binary' as const),
    }));
  }, [result.blobs]);

  if (files.length === 0) {
    return <Body1>No result files were produced.</Body1>;
  }

  return (
    <div className={styles.list}>
      {files.map((f) => (
        <FileCard
          key={f.name}
          name={f.name}
          kind={f.kind}
          text={blobs[f.name]}
        />
      ))}
    </div>
  );
}

function FileCard({
  name,
  kind,
  text,
}: {
  name: string;
  kind: 'json' | 'binary';
  text: string | undefined;
}) {
  const styles = useStyles();

  const sizeLabel =
    text !== undefined
      ? `${text.length.toLocaleString()} chars`
      : kind === 'binary'
        ? '(not loaded)'
        : '...';

  const display: string | null = useMemo(() => {
    if (text === undefined || kind !== 'json') return null;
    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      return text;
    }
  }, [text, kind]);

  return (
    <div className={styles.fileCard}>
      <div className={styles.fileHeader}>
        <span className={styles.fileName}>{name}</span>
        <span className={styles.fileMeta}>{sizeLabel}</span>
      </div>
      {kind === 'json' && text === undefined && (
        <Spinner size="extra-small" label="Loading..." />
      )}
      {display !== null && <pre className={styles.filePre}>{display}</pre>}
    </div>
  );
}

function toDataEntries(
  ctx: SyntheticRendererContext,
): CreateDataEntryRequest[] {
  const names = (ctx.result.blobs ?? []).filter(isJsonBlob);
  const missing = names.filter((n) => ctx.blobs[n] === undefined);
  if (missing.length > 0) {
    throw new Error(`Result file(s) not loaded yet: ${missing.join(', ')}`);
  }
  return names.map((name) => ({
    dataType: 'Generic',
    name,
    properties: { content: ctx.blobs[name] ?? '' },
  }));
}

/**
 * Build a fallback renderer bound to the given generationType. The shape is
 * intentionally identical across types — this just lets the registry return
 * something for any `run.generationType`.
 */
export function createBlobListRenderer(
  generationType: string,
): SyntheticResultRenderer {
  return {
    generationType,
    displayName: generationType,
    selectBlobsToLoad: (result) => (result.blobs ?? []).filter(isJsonBlob),
    Summary,
    Items,
    toDataEntries,
  };
}
